/**
 * Chart Info Icon Component
 * Displays an info icon that opens a popover with chart-specific information
 */
import React, { useState, useRef, useEffect } from 'react';

export default function ChartInfoIcon({ title, definition, limitations, futureMonthsNote = false }) {
  const [showPopover, setShowPopover] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: '28px', right: '0', left: 'auto', bottom: 'auto' });
  const iconRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target) && 
          iconRef.current && !iconRef.current.contains(event.target)) {
        setShowPopover(false);
      }
    };

    if (showPopover) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPopover]);

  useEffect(() => {
    if (showPopover && iconRef.current && popoverRef.current) {
      const iconRect = iconRef.current.getBoundingClientRect();
      const popoverWidth = 320;
      const popoverHeight = popoverRef.current.offsetHeight || 200;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      let top = '28px';
      let right = '0';
      
      // Check if popover would go off right edge
      if (iconRect.right - popoverWidth < 0) {
        right = 'auto';
        const left = iconRect.left + iconRect.width;
        if (left + popoverWidth > viewportWidth) {
          // Flip to left side
          right = `${iconRect.width}px`;
        }
      }
      
      // Check if popover would go off bottom edge
      if (iconRect.bottom + popoverHeight > viewportHeight) {
        // Flip to top
        top = 'auto';
        const bottom = viewportHeight - iconRect.top + 8;
        if (bottom < 0) {
          top = '28px';
        } else {
          top = 'auto';
          right = right === 'auto' ? '0' : right;
        }
      }
      
      setPopoverPosition({ top, right });
    }
  }, [showPopover]);

  return (
    <div style={styles.container}>
      <div
        ref={iconRef}
        style={styles.icon}
        onClick={() => setShowPopover(!showPopover)}
        title="Chart information"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4M12 8h.01" />
        </svg>
      </div>
      {showPopover && (
        <div ref={popoverRef} style={{
          ...styles.popover, 
          top: popoverPosition.top === 'auto' ? 'auto' : popoverPosition.top,
          bottom: popoverPosition.bottom || 'auto',
          right: popoverPosition.right === 'auto' ? 'auto' : popoverPosition.right,
          left: popoverPosition.left || 'auto'
        }}>
          <div style={styles.popoverHeader}>
            <h4 style={styles.popoverTitle}>{title}</h4>
            <button style={styles.closeButton} onClick={() => setShowPopover(false)}>×</button>
          </div>
          <div style={styles.popoverContent}>
            {definition && (
              <div style={styles.section}>
                <strong>Definition:</strong> {definition}
              </div>
            )}
            {limitations && (
              <div style={styles.section}>
                <strong>Notes:</strong> {limitations}
              </div>
            )}
            {futureMonthsNote && (
              <div style={styles.section}>
                <strong>Note:</strong> Future months may change as new reservations come in.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    position: 'relative',
    display: 'inline-block',
  },
  icon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: '#f3f4f6',
    color: '#6b7280',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  popover: {
    position: 'absolute',
    top: '28px',
    right: '0',
    width: '320px',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    zIndex: 10000, // Above sticky header (HEADER_Z_INDEX = 150) and all content
  },
  popoverHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid #e5e7eb',
  },
  popoverTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#111827',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    color: '#6b7280',
    cursor: 'pointer',
    padding: 0,
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  popoverContent: {
    padding: '12px 16px',
    fontSize: '12px',
    color: '#374151',
    lineHeight: '1.6',
  },
  section: {
    marginBottom: '12px',
  },
};
